<template>
  <div class="coupons-panel">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <div class="coupon-tabs">
        <button :class="{ active: couponStatusFilter === 'active' }" @click="setCouponFilter('active')">Activos</button>
        <button :class="{ active: couponStatusFilter === 'inactive' }" @click="setCouponFilter('inactive')">Inactivos</button>
      </div>
      <button class="btn-create" @click="showNewCouponModal = true" v-if="coupons.length > 0">
        <i class="fas fa-plus me-2"></i>Crear Cupón
      </button>
    </div>

    <!-- Coupons list -->
    <div>
      <div v-if="loading" class="text-center py-4">
        <i class="fas fa-spinner fa-spin fa-2x"></i>
        <p class="mt-2">Cargando cupones...</p>
      </div>
      <div v-else-if="coupons.length === 0" class="text-center py-4">
        <i class="fas fa-ticket-alt fa-2x mb-3" style="color:#555"></i>
        <p>No hay cupones registrados</p>
        <button class="btn-create mt-2" @click="showNewCouponModal = true">
          <i class="fas fa-plus me-2"></i>Crear tu primer cupón
        </button>
      </div>
      <div v-else class="table-responsive">
      <table class="coupons-table">
        <thead>          <tr>
            <th>Código</th>
            <th>Tipo</th>
            <th>Descuento</th>
            <th>Mín. compra</th>
            <th>Usos</th>
            <th>Válido hasta</th>
            <th>Estado</th>
            <th>Acciones</th>
          </tr>
        </thead>        <tbody>
          <tr v-for="(coupon, index) in filteredCoupons" :key="coupon?.id || index">            <td>{{ coupon.code }}</td>
            <td>{{ coupon.type === 'percentage' ? 'Porcentaje' : 'Monto fijo' }}</td>
            <td>{{ formatDiscount(coupon) }}</td>
            <td>{{ coupon.min_purchase ? '$' + formatAmount(coupon.min_purchase) : 'Sin mínimo' }}</td>
            <td>{{ coupon.used }}/{{ coupon.max_uses || '∞' }}</td>
            <td>{{ formatDate(coupon.expires_at) }}</td>
            <td>
              <span :class="['badge', coupon.active ? 'bg-success' : 'bg-danger']">
                {{ coupon.active ? 'Activo' : 'Inactivo' }}
              </span>
              <span v-if="coupon.distributors_only" class="badge bg-distributor d-block mt-1">Solo dist.</span>
            </td>            <td>              
              <button class="btn-action btn-action-info me-2" @click="viewCouponUsages(coupon)" title="Ver usuarios">
                <i class="fas fa-users"></i>
              </button>
              <button class="btn-action btn-action-edit me-2" @click="editCoupon(coupon)">
                <i class="fas fa-edit"></i>
              </button>
              <button class="btn-action btn-action-delete" @click="removeCoupon(coupon.id)">
                <i class="fas fa-trash"></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      </div>
    </div>

    <!-- New/Edit Coupon Modal -->
    <div class="modal" :class="{ 'show': showNewCouponModal }" v-if="showNewCouponModal">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ editingCoupon ? 'Editar Cupón' : 'Nuevo Cupón' }}</h5>
            <button type="button" class="btn-close" @click="closeModal"></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="saveCoupon">
              <!-- Código del cupón -->
              <div class="mb-3">
                <label class="form-label">Código</label>
                <input type="text" class="form-control" v-model="couponForm.code" required>
              </div>
              
              <!-- Tipo de descuento -->
              <div class="mb-3">
                <label class="form-label">Tipo de descuento</label>
                <select class="form-select" v-model="couponForm.type">
                  <option value="percentage">Porcentaje</option>
                  <option value="fixed">Monto fijo</option>
                </select>
              </div>
              
              <!-- Valor del descuento -->
              <div class="mb-3">
                <label class="form-label">Valor</label>
                <div class="input-group">
                  <input type="number" class="form-control" v-model="couponForm.value" min="0" :max="couponForm.type === 'percentage' ? 100 : null" step="0.01" required>
                  <span class="input-group-text">{{ couponForm.type === 'percentage' ? '%' : '$' }}</span>
                </div>
              </div>

              <!-- Categorías aplicables -->
              <div class="mb-3">
                <label class="form-label">Categorías (opcional)</label>
                <select class="form-select" v-model="couponForm.categories" multiple>
                  <option v-for="category in categories" :key="category.id" :value="category.id">
                    {{ category.name }}
                  </option>
                </select>
                <small class="form-text text-muted">
                  Si no selecciona ninguna categoría, el cupón se aplicará a todos los productos.
                  Puede seleccionar múltiples categorías manteniendo presionada la tecla Ctrl/Cmd.
                </small>
              </div>
              
              <!-- Monto máximo (solo para descuentos porcentuales) -->
              <div class="mb-3" v-if="couponForm.type === 'percentage'">
                <label class="form-label">Monto máximo de descuento (opcional)</label>
                <div class="input-group">
                  <span class="input-group-text">$</span>
                  <input type="number" class="form-control" v-model="couponForm.max_amount" min="0" step="0.01">
                </div>
              </div>
              
              <!-- Monto mínimo de compra -->
              <div class="mb-3">
                <label class="form-label">Monto mínimo de compra (opcional)</label>
                <div class="input-group">
                  <span class="input-group-text">$</span>
                  <input type="number" class="form-control" v-model="couponForm.min_purchase" min="0" step="0.01">
                </div>
              </div>
              
              <!-- Máximo de usos -->
              <div class="mb-3">
                <label class="form-label">Máximo de usos (opcional)</label>
                <input type="number" class="form-control" v-model="couponForm.max_uses" min="1">
              </div>
              
              <!-- Fecha de vencimiento -->
              <div class="mb-3">
                <label class="form-label">Válido hasta</label>
                <input type="date" class="form-control" v-model="couponForm.expires_at" :min="minDate" required>
              </div>
              
              <!-- Solo mayoristas -->
              <div class="mb-3">
                <div class="distributor-toggle-card">
                  <div class="form-check form-switch mb-0">
                    <input type="checkbox" class="form-check-input" v-model="couponForm.distributors_only" id="distributorsCheck" role="switch">
                    <label class="form-check-label distributor-toggle-label" for="distributorsCheck">Solo para mayoristas</label>
                  </div>
                  <small class="distributor-toggle-hint">
                    Si está activado, solo los clientes con precio mayorista podrán usar este cupón.
                  </small>
                </div>
              </div>

              <!-- Estado activo/inactivo -->
              <div class="mb-3">
                <div class="form-check">
                  <input type="checkbox" class="form-check-input" v-model="couponForm.active" id="activeCheck">
                  <label class="form-check-label" for="activeCheck">Cupón activo</label>
                </div>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn-cancel" @click="closeModal">Cancelar</button>
            <button type="button" class="btn-save" @click="saveCoupon" :disabled="saving">
              <i class="fas fa-spinner fa-spin me-2" v-if="saving"></i>
              {{ saving ? 'Guardando...' : 'Guardar' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal para mostrar usuarios que usaron un cupón -->
    <div class="modal" :class="{ 'show': showUsageModal }" v-if="showUsageModal">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              Usuarios que usaron el cupón: <span class="coupon-code-highlight">{{ selectedCoupon?.code }}</span>
            </h5>
            <button type="button" class="btn-close" @click="closeUsageModal"></button>
          </div>
          <div class="modal-body">
            <div v-if="loadingUsages" class="text-center py-4">
              <i class="fas fa-spinner fa-spin fa-2x"></i>
              <p class="mt-2">Cargando usuarios...</p>
            </div>
            <div v-else-if="couponUsages.length === 0" class="text-center py-4">
              <i class="fas fa-users fa-2x mb-3"></i>
              <p>Este cupón aún no ha sido utilizado por ningún usuario</p>
            </div>
            <div v-else class="table-responsive">
              <table class="coupons-table">                <thead>
                  <tr>
                    <th>Usuario</th>
                    <th>Email</th>
                    <th>Fecha de uso</th>
                    <th>Orden</th>
                    <th>Monto compra</th>
                    <th>Descuento</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="usage in couponUsages" :key="usage.id">
                    <td>{{ usage.user.first_name }} {{ usage.user.last_name }}</td>
                    <td>{{ usage.user.email }}</td>
                    <td>{{ formatDateTime(usage.used_at) }}</td>
                    <td>
                      <a :href="`/configuracion/pedidos/${usage.order_id}`" class="order-link">
                        {{ usage.order_id }}
                      </a>
                    </td>
                    <td>{{ usage.purchase_amount ? '$' + formatAmount(usage.purchase_amount) : 'N/A' }}</td>
                    <td>{{ usage.amount ? '$' + formatAmount(usage.amount) : 'N/A' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn-cancel" @click="closeUsageModal">Cerrar</button>
          </div>
        </div>
      </div>
    </div>

    <section class="roulette-admin">
      <div class="roulette-admin__header">
        <div>
          <h5>Premios de la ruleta</h5>
          <p>Configura descuentos, topes, duracion y probabilidad.</p>
        </div>
        <button class="btn-create" @click="openPrizeModal">
          <i class="fas fa-plus me-2"></i>Crear premio
        </button>
      </div>

      <div class="table-responsive">
        <table class="coupons-table">
          <thead>
            <tr>
              <th>Premio</th>
              <th>Tipo</th>
              <th>Valor</th>
              <th>Tope</th>
              <th>Duracion</th>
              <th>Categorias</th>
              <th>Peso</th>
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="prize in roulettePrizes" :key="prize.id">
              <td>{{ prize.label }}</td>
              <td>{{ prize.type === 'percentage' ? 'Porcentaje' : 'Monto fijo' }}</td>
              <td>{{ prize.type === 'percentage' ? prize.value + '%' : '$' + formatAmount(prize.value) }}</td>
              <td>{{ prize.max_amount ? '$' + formatAmount(prize.max_amount) : '-' }}</td>
              <td>{{ prize.duration_minutes }} min</td>
              <td>{{ formatCategoryList(prize.categories) }}</td>
              <td>{{ prize.weight }}</td>
              <td>
                <span :class="['badge', prize.active ? 'bg-success' : 'bg-danger']">
                  {{ prize.active ? 'Activo' : 'Inactivo' }}
                </span>
              </td>
              <td>
                <button class="btn-action btn-action-edit me-2" @click="editPrize(prize)">
                  <i class="fas fa-edit"></i>
                </button>
                <button class="btn-action btn-action-delete" @click="removePrize(prize.id)">
                  <i class="fas fa-trash"></i>
                </button>
              </td>
            </tr>
            <tr v-if="roulettePrizes.length === 0">
              <td colspan="9" class="text-center">No hay premios configurados</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <div class="modal" :class="{ 'show': showPrizeModal }" v-if="showPrizeModal">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ editingPrize ? 'Editar premio' : 'Nuevo premio' }}</h5>
            <button type="button" class="btn-close" @click="closePrizeModal"></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label">Etiqueta</label>
              <input class="form-control" v-model="prizeForm.label" required>
            </div>
            <div class="mb-3">
              <label class="form-label">Tipo</label>
              <select class="form-select" v-model="prizeForm.type">
                <option value="percentage">Porcentaje</option>
                <option value="fixed">Monto fijo</option>
              </select>
            </div>
            <div class="mb-3">
              <label class="form-label">Valor</label>
              <input class="form-control" type="number" min="0" :max="prizeForm.type === 'percentage' ? 100 : null" step="0.01" v-model="prizeForm.value">
            </div>
            <div class="mb-3" v-if="prizeForm.type === 'percentage'">
              <label class="form-label">Tope de descuento</label>
              <input class="form-control" type="number" min="0" step="0.01" v-model="prizeForm.max_amount">
            </div>
            <div class="mb-3">
              <label class="form-label">Monto minimo</label>
              <input class="form-control" type="number" min="0" step="0.01" v-model="prizeForm.min_purchase">
            </div>
            <div class="mb-3">
              <label class="form-label">Duracion del cupon (minutos)</label>
              <input class="form-control" type="number" min="5" v-model="prizeForm.duration_minutes">
            </div>
            <div class="mb-3">
              <label class="form-label">Categorias aplicables</label>
              <select class="form-select" v-model="prizeForm.categories" multiple>
                <option v-for="category in categories" :key="category.id" :value="category.id">
                  {{ getCategoryDisplayName(category) }}
                </option>
              </select>
              <small class="form-text text-muted">
                Si no seleccionas categorias, el cupon de ruleta aplicara a todas excepto celulares.
                Los productos de tipo celular quedan excluidos siempre.
              </small>
            </div>
            <div class="mb-3">
              <label class="form-label">Peso/probabilidad</label>
              <input class="form-control" type="number" min="1" v-model="prizeForm.weight">
            </div>
            <div class="mb-3">
              <label class="form-label">Prefijo del codigo</label>
              <input class="form-control" v-model="prizeForm.code_prefix" maxlength="12">
            </div>
            <div class="mb-3">
              <label class="form-label">Colores</label>
              <div class="d-flex gap-2">
                <input class="form-control form-control-color" type="color" v-model="prizeForm.color">
                <input class="form-control form-control-color" type="color" v-model="prizeForm.text_color">
              </div>
            </div>
            <div class="form-check">
              <input type="checkbox" class="form-check-input" v-model="prizeForm.active" id="prizeActiveCheck">
              <label class="form-check-label" for="prizeActiveCheck">Premio activo</label>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn-cancel" @click="closePrizeModal">Cancelar</button>
            <button type="button" class="btn-save" @click="savePrize" :disabled="savingPrize">
              <i class="fas fa-spinner fa-spin me-2" v-if="savingPrize"></i>
              {{ savingPrize ? 'Guardando...' : 'Guardar' }}
            </button>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useToast } from 'vue-toastification';
import {
  getCoupons,
  createCoupon,
  updateCoupon,
  deleteCoupon,
  getCouponUsages,
  getRoulettePrizes,
  createRoulettePrize,
  updateRoulettePrize,
  deleteRoulettePrize
} from '@/services/coupons';
import { getCategories } from '@/services/marketing';

const toast = useToast();

const showNewCouponModal = ref(false);
const showUsageModal = ref(false);
const saving = ref(false);
const editingCoupon = ref(null);
const loading = ref(true);
const loadingUsages = ref(false);
const coupons = ref([]);
const couponUsages = ref([]);
const selectedCoupon = ref(null);
const categories = ref([]);
const couponStatusFilter = ref('active');
const roulettePrizes = ref([]);
const showPrizeModal = ref(false);
const editingPrize = ref(null);
const savingPrize = ref(false);

const prizeForm = ref({
  label: '',
  type: 'percentage',
  value: 10,
  max_amount: null,
  min_purchase: 0,
  duration_minutes: 60,
  max_uses_per_coupon: 1,
  weight: 1,
  code_prefix: 'RLT',
  categories: [],
  color: '#d60000',
  text_color: '#ffffff',
  active: true,
  display_order: 0
});

const couponForm = ref({
  code: '',
  type: 'percentage',
  value: 0,
  categories: [],
  max_uses: null,
  expires_at: (() => {
    const date = new Date();
    date.setDate(date.getDate() + 30);
    return date.toISOString().split('T')[0];
  })(),
  active: true,
  max_amount: null,
  min_purchase: 0,
  distributors_only: false
});

const minDate = computed(() => {
  const today = new Date();
  return today.toISOString().split('T')[0];
});

// Propiedad computada para filtrar cupones nulos
const filteredCoupons = computed(() => {
  return coupons.value.filter(coupon => coupon !== null);
});

// Función para cargar todos los cupones
const loadCoupons = async () => {
  try {
    loading.value = true;
    const data = await getCoupons({ active: couponStatusFilter.value === 'active' });
    // Asegurarnos de que data es un array antes de asignarlo
    coupons.value = Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('Error al cargar cupones:', error);
    toast.error('Error al cargar la lista de cupones');
    // Inicializar como array vacío en caso de error
    coupons.value = [];
  } finally {
    loading.value = false;
  }
};

// Cargar cupones cuando se monte el componente
onMounted(() => {
  loadCoupons();
  loadRoulettePrizes();
  loadCategories(); // Cargar categorías al montar el componente
});

const setCouponFilter = async (filter) => {
  couponStatusFilter.value = filter;
  await loadCoupons();
};

const loadRoulettePrizes = async () => {
  try {
    const data = await getRoulettePrizes();
    roulettePrizes.value = Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('Error al cargar premios de ruleta:', error);
    toast.error('Error al cargar los premios de la ruleta');
    roulettePrizes.value = [];
  }
};

const loadCategories = async () => {
  try {
    const data = await getCategories();
    categories.value = Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('Error al cargar categorías:', error);
    toast.error('Error al cargar las categorías');
    categories.value = [];
  }
};

const formatDiscount = (coupon) => {
  if (!coupon) return '-';
  let discount = coupon.type === 'percentage' ? `${coupon.value}%` : `$${coupon.value}`;
  if (coupon.type === 'percentage' && coupon.max_amount) {
    discount += ` (hasta $${coupon.max_amount})`;
  }
  return discount;
};

const formatDate = (date) => {
  if (!date) return '-';
  try {
    return new Date(date).toLocaleDateString('es-AR');
  } catch (error) {
    console.error('Error al formatear fecha:', error);
    return '-';
  }
};

const formatDateTime = (date) => {
  if (!date) return '-';
  try {
    const dateObj = new Date(date);
    return dateObj.toLocaleDateString('es-AR') + ' ' + dateObj.toLocaleTimeString('es-AR');
  } catch (error) {
    console.error('Error al formatear fecha y hora:', error);
    return '-';
  }
};

const formatAmount = (amount) => {
  const number = Number(amount || 0);
  return number.toLocaleString('es-CL');
};

const getCategoryDisplayName = (category) => {
  if (!category) return '';
  return category.parent_name ? `${category.parent_name} > ${category.name}` : category.name;
};

const formatCategoryList = (categoryIds) => {
  if (!categoryIds || categoryIds.length === 0) {
    return 'Todas excepto celulares';
  }
  const names = categoryIds
    .map(id => categories.value.find(category => String(category.id) === String(id)))
    .filter(Boolean)
    .map(getCategoryDisplayName);
  return names.length > 0 ? names.join(', ') : `${categoryIds.length} configuradas`;
};

const closeModal = () => {
  showNewCouponModal.value = false;
  editingCoupon.value = null;
  
  // Fecha de vencimiento predeterminada: 30 días a partir de hoy
  const defaultExpiryDate = new Date();
  defaultExpiryDate.setDate(defaultExpiryDate.getDate() + 30);
  
  couponForm.value = {
    code: '',
    type: 'percentage',
    value: 0,
    categories: [],
    max_uses: null,
    expires_at: defaultExpiryDate.toISOString().split('T')[0],
    active: true,
    max_amount: null,
    min_purchase: 0,
    distributors_only: false
  };
};

const viewCouponUsages = async (coupon) => {
  selectedCoupon.value = coupon;
  showUsageModal.value = true;
  
  try {
    loadingUsages.value = true;
    const data = await getCouponUsages(coupon.id);
    couponUsages.value = Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('Error al cargar los usos del cupón:', error);
    toast.error('Error al cargar la información de usuarios');
    couponUsages.value = [];
  } finally {
    loadingUsages.value = false;
  }
};

const closeUsageModal = () => {
  showUsageModal.value = false;
  selectedCoupon.value = null;
  couponUsages.value = [];
};

const saveCoupon = async () => {
  try {
    saving.value = true;
    
    // Asegurarse de que la fecha sea en formato ISO y se incluya la hora al final del día
    // para que expire al final del día seleccionado
    const expiryDate = new Date(couponForm.value.expires_at);
    expiryDate.setHours(23, 59, 59, 999);
    
    const formData = {
      ...couponForm.value,
      expires_at: expiryDate.toISOString()
    };
    
    // Convertir los valores numéricos explícitamente
    if (formData.value) {
      formData.value = parseFloat(formData.value);
    }
    
    if (formData.max_amount) {
      formData.max_amount = parseFloat(formData.max_amount);
    } else {
      formData.max_amount = null; // Asegurarse de que sea null y no undefined
    }
      if (formData.max_uses) {
      formData.max_uses = parseInt(formData.max_uses);
    } else {
      formData.max_uses = null; // Asegurarse de que sea null y no undefined
    }
    
    // Asegurar que min_purchase sea un número (al menos 0)
    if (formData.min_purchase !== undefined && formData.min_purchase !== null) {
      formData.min_purchase = parseFloat(formData.min_purchase);
    } else {
      formData.min_purchase = 0; // Valor por defecto: sin mínimo
    }
    
    console.log('Datos a enviar:', formData);
    
    if (editingCoupon.value) {
      await updateCoupon(editingCoupon.value.id, formData);
      toast.success('Cupón actualizado correctamente');
    } else {
      await createCoupon(formData);
      toast.success('Cupón creado correctamente');
    }
    
    await loadCoupons(); // Recargar la lista de cupones
    closeModal();
  } catch (error) {
    console.error('Error al guardar cupón:', error);
    let errorMsg = 'Error al guardar el cupón';
    if (error.response && error.response.data) {
      console.error('Detalle del error:', error.response.data);
      if (typeof error.response.data === 'object') {
        const firstError = Object.entries(error.response.data)[0];
        if (firstError) {
          const fieldName = firstError[0];
          const errorMessages = Array.isArray(firstError[1]) ? firstError[1].join('. ') : firstError[1];
          errorMsg = `${fieldName}: ${errorMessages}`;
        }
      } else if (error.response.data.detail) {
        errorMsg = error.response.data.detail;
      }
    }
    toast.error(errorMsg);
  } finally {
    saving.value = false;
  }
};

const editCoupon = (coupon) => {
  editingCoupon.value = coupon;
  
  // Formateamos la fecha para que sea compatible con el input de tipo date
  let expiresAt = coupon.expires_at;
  if (expiresAt) {
    const date = new Date(expiresAt);
    expiresAt = date.toISOString().split('T')[0];
  }
  
  couponForm.value = {
    ...coupon,
    expires_at: expiresAt,
    value: parseFloat(coupon.value),
    categories: coupon.categories || [],
    max_amount: coupon.max_amount ? parseFloat(coupon.max_amount) : null,
    max_uses: coupon.max_uses ? parseInt(coupon.max_uses) : null,
    min_purchase: coupon.min_purchase ? parseFloat(coupon.min_purchase) : 0,
    distributors_only: !!coupon.distributors_only
  };
  
  showNewCouponModal.value = true;
};

const removeCoupon = async (id) => {
  if (!confirm('¿Estás seguro de eliminar este cupón?')) return;
  
  try {
    await deleteCoupon(id);
    await loadCoupons(); // Recargar la lista de cupones
    toast.success('Cupón eliminado correctamente');
  } catch (error) {
    console.error('Error al eliminar cupón:', error);
    toast.error('Error al eliminar el cupón');
  }
};
const resetPrizeForm = () => {
  editingPrize.value = null;
  prizeForm.value = {
    label: '',
    type: 'percentage',
    value: 10,
    max_amount: null,
    min_purchase: 0,
    duration_minutes: 60,
    max_uses_per_coupon: 1,
    weight: 1,
    code_prefix: 'RLT',
    categories: [],
    color: '#d60000',
    text_color: '#ffffff',
    active: true,
    display_order: roulettePrizes.value.length
  };
};

const openPrizeModal = () => {
  resetPrizeForm();
  showPrizeModal.value = true;
};

const editPrize = (prize) => {
  editingPrize.value = prize;
  prizeForm.value = {
    ...prize,
    value: parseFloat(prize.value),
    max_amount: prize.max_amount ? parseFloat(prize.max_amount) : null,
    min_purchase: prize.min_purchase ? parseFloat(prize.min_purchase) : 0,
    duration_minutes: parseInt(prize.duration_minutes || 60),
    max_uses_per_coupon: parseInt(prize.max_uses_per_coupon || 1),
    weight: parseInt(prize.weight || 1),
    categories: prize.categories || [],
    display_order: parseInt(prize.display_order || 0)
  };
  showPrizeModal.value = true;
};

const closePrizeModal = () => {
  showPrizeModal.value = false;
  resetPrizeForm();
};

const savePrize = async () => {
  try {
    savingPrize.value = true;
    const payload = {
      ...prizeForm.value,
      value: parseFloat(prizeForm.value.value),
      max_amount: prizeForm.value.type === 'percentage' && prizeForm.value.max_amount ? parseFloat(prizeForm.value.max_amount) : null,
      min_purchase: prizeForm.value.min_purchase ? parseFloat(prizeForm.value.min_purchase) : 0,
      duration_minutes: parseInt(prizeForm.value.duration_minutes),
      max_uses_per_coupon: parseInt(prizeForm.value.max_uses_per_coupon),
      weight: parseInt(prizeForm.value.weight),
      categories: prizeForm.value.categories || [],
      display_order: parseInt(prizeForm.value.display_order || 0)
    };

    if (editingPrize.value) {
      await updateRoulettePrize(editingPrize.value.id, payload);
      toast.success('Premio actualizado correctamente');
    } else {
      await createRoulettePrize(payload);
      toast.success('Premio creado correctamente');
    }
    await loadRoulettePrizes();
    closePrizeModal();
  } catch (error) {
    console.error('Error al guardar premio:', error);
    toast.error('Error al guardar el premio de la ruleta');
  } finally {
    savingPrize.value = false;
  }
};

const removePrize = async (id) => {
  if (!confirm('Eliminar este premio de la ruleta?')) return;
  try {
    await deleteRoulettePrize(id);
    await loadRoulettePrizes();
    toast.success('Premio eliminado correctamente');
  } catch (error) {
    console.error('Error al eliminar premio:', error);
    toast.error('Error al eliminar el premio');
  }
};
</script>

<style scoped>
/* ===== Container ===== */
.coupons-panel {
  padding: 1rem;
}

/* ===== Create Button ===== */
.btn-create {
  background: var(--primary-color);
  border: none;
  color: #fff;
  font-size: 0.84rem;
  font-weight: 600;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  transition: all 0.15s ease;
}
.btn-create:hover { background: var(--primary-dark); color: #fff; }

.coupon-tabs {
  display: inline-flex;
  gap: 0.35rem;
  padding: 0.25rem;
  border-radius: 8px;
  background: rgba(0,0,0,0.22);
  border: 1px solid rgba(255,255,255,0.08);
}
.coupon-tabs button {
  border: 0;
  border-radius: 6px;
  padding: 0.42rem 0.8rem;
  background: transparent;
  color: #888;
  font-size: 0.82rem;
  font-weight: 700;
}
.coupon-tabs button.active {
  background: var(--primary-color);
  color: #fff;
}

.roulette-admin {
  margin-top: 2rem;
  padding-top: 1.25rem;
  border-top: 1px solid rgba(255,255,255,0.08);
}
.roulette-admin__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}
.roulette-admin__header h5 {
  margin: 0;
  color: #ddd;
  font-size: 0.98rem;
  font-weight: 700;
}
.roulette-admin__header p {
  margin: 0.2rem 0 0;
  color: #777;
  font-size: 0.82rem;
}

/* ===== Table ===== */
.coupons-table {
  color: #aaa;
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}

.coupons-table thead th {
  background: transparent;
  border: none;
  border-bottom: 1px solid rgba(255,255,255,0.06);
  color: #666;
  font-weight: 600;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  padding: 0.65rem 0.75rem;
}

.coupons-table td {
  border-color: rgba(255,255,255,0.04);
  vertical-align: middle;
  font-size: 0.84rem;
  padding: 0.75rem;
}

/* ===== Badges ===== */
.badge { padding: 0.35em 0.6em; font-size: 0.72rem; border-radius: 6px; }
.badge.bg-success { background: rgba(var(--success-color-rgb), 0.2) !important; color: var(--success-color); }
.badge.bg-danger { background: rgba(248,113,113,0.15) !important; color: #f87171; }

/* ===== Action Buttons ===== */
.btn-action {
  font-size: 0.78rem;
  padding: 0.3rem 0.6rem;
  border-radius: 6px;
  transition: all 0.15s ease;
}

.btn-action-info {
  background: rgba(0,0,0,0.2);
  border: 1px solid rgba(96,165,250,0.2);
  color: #60a5fa;
}
.btn-action-info:hover { background: rgba(96,165,250,0.08); border-color: rgba(96,165,250,0.3); color: #60a5fa; }

.btn-action-edit {
  background: rgba(0,0,0,0.2);
  border: 1px solid rgba(255,255,255,0.1);
  color: #888;
}
.btn-action-edit:hover { color: #ccc; border-color: rgba(255,255,255,0.15); }

.btn-action-delete {
  background: rgba(0,0,0,0.2);
  border: 1px solid rgba(248,113,113,0.2);
  color: #f87171;
}
.btn-action-delete:hover { background: rgba(248,113,113,0.08); border-color: rgba(248,113,113,0.3); }

/* ===== Modal ===== */
.modal {
  display: none;
  background-color: rgba(0,0,0,0.6);
}
.modal.show { display: block; }

.modal-content {
  background: #1a1a1a;
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 10px;
  color: #aaa;
}

.modal-header {
  border-bottom: 1px solid rgba(255,255,255,0.06);
  padding: 1rem 1.25rem;
}

.modal-title {
  color: #ccc;
  font-size: 0.95rem;
  font-weight: 600;
}

.modal-footer {
  border-top: 1px solid rgba(255,255,255,0.06);
  padding: 0.75rem 1.25rem;
}

.btn-close {
  filter: invert(1) grayscale(100%) brightness(200%);
}

/* ===== Forms ===== */
.form-label {
  color: #888;
  font-size: 0.82rem;
  font-weight: 600;
}

.form-control, .form-select {
  background: rgba(0,0,0,0.3);
  border: 1px solid rgba(255,255,255,0.1);
  color: #ccc;
  font-size: 0.84rem;
  border-radius: 8px;
  transition: all 0.15s ease;
}
.form-control::placeholder { color: #444; }
.form-control:focus, .form-select:focus {
  background: rgba(0,0,0,0.35);
  border-color: rgba(var(--primary-color-rgb), 0.4);
  box-shadow: 0 0 0 2px rgba(var(--primary-color-rgb), 0.15);
  color: #ddd;
}

.input-group-text {
  background: rgba(0,0,0,0.25);
  border: 1px solid rgba(255,255,255,0.1);
  color: #888;
  font-size: 0.84rem;
}

.form-text.text-muted {
  color: #555 !important;
  font-size: 0.78rem;
}

.form-check-input {
  background-color: rgba(0,0,0,0.3);
  border-color: rgba(255,255,255,0.15);
}
.form-check-input:checked {
  background-color: var(--primary-color);
  border-color: var(--primary-color);
}
.form-check-label {
  color: #aaa;
  font-size: 0.84rem;
}

/* ===== Modal Buttons ===== */
.btn-cancel {
  background: rgba(0,0,0,0.2);
  border: 1px solid rgba(255,255,255,0.1);
  color: #888;
  font-size: 0.84rem;
  padding: 0.45rem 0.85rem;
  border-radius: 8px;
  transition: all 0.15s ease;
}
.btn-cancel:hover { color: #ccc; border-color: rgba(255,255,255,0.15); }

.btn-save {
  background: var(--primary-color);
  border: none;
  color: #fff;
  font-size: 0.84rem;
  font-weight: 600;
  padding: 0.45rem 0.85rem;
  border-radius: 8px;
  transition: all 0.15s ease;
}
.btn-save:hover:not(:disabled) { background: var(--primary-dark); color: #fff; }
.btn-save:disabled { opacity: 0.5; color: #fff; }

/* ===== Distributor Toggle ===== */
.distributor-toggle-card {
  background: rgba(0,0,0,0.18);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 10px;
  padding: 0.85rem 1rem;
}
.distributor-toggle-label {
  color: #ddd !important;
  font-size: 0.88rem !important;
  font-weight: 600 !important;
  cursor: pointer;
  margin-left: 0.35rem;
}
.form-check-input[role="switch"] {
  width: 2.4rem;
  height: 1.15rem;
  cursor: pointer;
}
.distributor-toggle-hint {
  display: block;
  margin-top: 0.5rem;
  font-size: 0.78rem;
  color: #777;
  line-height: 1.45;
}
.badge.bg-distributor {
  background: rgba(96,165,250,0.12) !important;
  color: #93c5fd;
  border: 1px solid rgba(96,165,250,0.2);
  font-size: 0.68rem;
}

/* ===== Misc ===== */
.coupon-code-highlight { color: var(--primary-color); font-weight: 600; }
.order-link { color: var(--primary-color); text-decoration: none; }
.order-link:hover { color: var(--primary-light); text-decoration: underline; }

.spinner-border, .fa-spinner { color: var(--primary-color) !important; }

.text-center p { color: #888; font-size: 0.84rem; }

/* ===== Responsive ===== */
@media (max-width: 768px) {
  .coupons-panel { padding: 0.5rem; }

  .table-responsive { overflow-x: auto; -webkit-overflow-scrolling: touch; }
  .coupons-table { min-width: 700px; }

  .coupons-table th,
  .coupons-table td {
    font-size: 0.78rem;
    padding: 0.5rem 0.5rem;
  }
}

@media (max-width: 576px) {
  .coupons-table { min-width: 600px; }
}
</style>
